import { useCallback, useState, useRef, ChangeEvent, KeyboardEvent, FC } from 'react';

import FeaturesManager, { FEATURES } from '@belong/feature-toggles';
import { Hidden } from '@belong/ui-core';
import { logger } from '@belong/logging';

import { LIVE_CHAT_TEST_IDS } from '../../lib/testIds';
import { ACCEPTED_FILE_TYPES, isValidFileSize } from '../../lib/attachment';
import { IChatProps } from '../Chat';
import CONTENT from '../content.json';

import { AttachmentStatus, AttachmentUploader } from '../Attachment';
import { ChatInputContainer, InputFocusOutline, InputWrapper, StyledInput } from './ChatInput.styles';
import { StyledSendButton } from '../elements';

type ChatInputProps = Pick<
  IChatProps,
  'onTypeMessage' | 'onSendMessage' | 'onSendAttachment' | 'isScanningAttachment'
> & {
  canAddAttachment?: boolean;
  disabled?: boolean;
  setAddAttachmentVisible(visible: boolean): void;
};

export const ChatInput: FC<ChatInputProps> = ({
  onTypeMessage,
  onSendMessage,
  onSendAttachment,
  disabled,
  canAddAttachment,
  isScanningAttachment,
  setAddAttachmentVisible
}) => {
  const [text, setText] = useState('');
  const textInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setError] = useState<string>();
  const attachmentRef = useRef<HTMLInputElement>(null);

  const dismissError = () => {
    setError('');
    textInputRef?.current?.focus();
  };

  const handleError = useCallback((error: Error | string) => {
    logger.error('UploaderContent', 'Upload error', error);

    // Depending on the error that is returned, we need to show a corresponding error message
    setError(typeof error === 'string' ? error : CONTENT.uploadStatus.error.generic);
    setIsUploading(false);

    // If there was an error, we need to clear the selected file to allow the user to try again with the same file
    if (attachmentRef.current?.value) {
      attachmentRef.current.value = '';
    }
  }, []);

  const sendText = useCallback(() => {
    textInputRef?.current?.focus();
    onSendMessage?.(text);
    setText('');
  }, [text, textInputRef, onSendMessage]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newText = e.currentTarget.value;
      onTypeMessage?.(newText);
      setText(newText);
    },
    [onTypeMessage]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        sendText();
      }
    },
    [sendText]
  );

  const handleFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (isUploading) {
        return;
      }

      // Remove any errors that are visible so we can show the uploading status
      setError('');

      const file = event.target.files?.[0];
      if (file) {
        if (!isValidFileSize(file)) {
          handleError(CONTENT.uploadStatus.error.exceedSizeLimit);
          return;
        }

        setIsUploading(true);
        try {
          const response = await onSendAttachment(file);
          if (response?.error) {
            handleError(response.error);
            return;
          }

          setIsUploading(false);

          // Once the upload is complete, we need to clear the selected file
          if (attachmentRef.current?.value) {
            attachmentRef.current.value = '';
          }
        } catch (e) {
          handleError(e);
        }
      }
    },
    [handleError, onSendAttachment, isUploading]
  );

  const renderAttachementButton = FeaturesManager.isFeatureOn(FEATURES.LIVE_CHAT_ATTACHMENTS) && canAddAttachment;
  const attachmentLoading = isUploading || isScanningAttachment;
  const statusVisible = !!uploadError || attachmentLoading;

  return (
    <>
      <ChatInputContainer>
        {renderAttachementButton && (
          <AttachmentUploader
            attachmentRef={attachmentRef}
            setAddAttachmentVisible={setAddAttachmentVisible}
            attachmentLoading={attachmentLoading}
          />
        )}
        <InputWrapper statusVisible={statusVisible} disabled={disabled}>
          <StyledInput
            data-testid={LIVE_CHAT_TEST_IDS.TEXT_INPUT}
            disabled={disabled}
            placeholder={CONTENT.input.placeholder[disabled ? 'disabled' : 'active']}
            attachmentButtonVisible={renderAttachementButton}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            value={text}
            enterKeyHint="send"
            ref={textInputRef}
          />
          <InputFocusOutline />
          {!disabled ? (
            <StyledSendButton
              data-testid={LIVE_CHAT_TEST_IDS.SEND_BUTTON}
              aria-label={CONTENT.ariaLabels.send}
              onClick={sendText}
            />
          ) : null}
        </InputWrapper>
      </ChatInputContainer>
      <AttachmentStatus dismissError={dismissError} error={uploadError} isLoading={attachmentLoading} />
      <Hidden atAndAbove="xs">
        <input accept={ACCEPTED_FILE_TYPES.join()} onChange={handleFileSelect} type="file" ref={attachmentRef} />
      </Hidden>
    </>
  );
};

ChatInput.displayName = 'ChatInput';
